// main imports
import React, { useMemo } from "react";

// plugin imports
import { graphql, useStaticQuery } from "gatsby";
import { Link } from "@herob191/gatsby-plugin-react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

// style imports
import "./layout.scss";
import "../../styles/main.scss";
import "swiper/css";

// svg imports
import Icon from "../../svg/icon.svg";
import Logotype from "../../svg/logotype.svg";

// components imports
import { LayoutContext } from "../../context/layoutContext";
import CookiesBanner from "./cookiesBanner/cookiesBanner";
import SmoothScroll from "./smoothScroll";
import Footer from "./footer/footer";
import Marquee from "../marquee/marquee";
import NavMenuButton from "./navMenu/navMenuButton";
import NavMenu from "./navMenu/navMenu";

// util imports
import { slideFade } from "../../utils/motionAnimations";
import useTranslate from "../../utils/useTranslate";
import useRestaurantDetails from "../../utils/restaurantDetails";
import DiagonalReveal from "../motion/parallax/diagonalReveal/diagonalReveal";

// component
export default function Layout({ children }) {
	const { allIllustrations } = useStaticQuery(graphql`
		query {
			allIllustrations: allFile(
				filter: {
					extension: { eq: "png" }
					absolutePath: { regex: "/illustrations/" }
				}
				sort: { name: ASC }
			) {
				nodes {
					name
					relativeDirectory
					childImageSharp {
						gatsbyImageData
					}
				}
			}
		}
	`);

	const { restaurantId, showNavLogotype, setNavMenuOpen } =
		React.useContext(LayoutContext);

	const restaurant = useRestaurantDetails(restaurantId);
	const restaurantTitle = useTranslate(restaurant?.frontmatter?.title);
	const restaurantIllustrations = useMemo(
		() =>
			allIllustrations.nodes?.filter((image) => {
				return image?.relativeDirectory?.includes(restaurantId);
			}),
		[restaurantId, allIllustrations],
	);

	const hideMenu = () => {
		setNavMenuOpen(false);
	};

	return (
		<SmoothScroll>
			<div className={`${restaurant ? restaurant.fields.id : ""}`}>
				<NavMenu />

				<nav className="padding">
					<div className="navBarTop border blurBg">
						<NavMenuButton />

						<Link
							to="/"
							className={`navLogotype ${showNavLogotype ? "" : "hide"}`}
							onClick={hideMenu}
						>
							<Logotype />
						</Link>

						<Link to="/" className="navIcon" onClick={hideMenu}>
							<Icon />
						</Link>
					</div>
				</nav>

				<main>
					<AnimatePresence mode="wait">
						<motion.div
							className="restaurantTopMarquee"
							key={restaurant?.fields?.id + "titleMarquee"}
							initial="hidden"
							animate="show"
							exit="exit"
							{...slideFade}
						>
							<Marquee>
								{restaurantTitle && (
									<span className="h1">Goisushi {restaurantTitle}</span>
								)}
							</Marquee>
						</motion.div>
					</AnimatePresence>
					<AnimatePresence mode="wait">
						<motion.div
							className="restaurantTopIllustrations"
							key={restaurant?.fields?.id + "illustrations"}
							initial="hidden"
							animate="show"
							exit="exit"
							transition={{
								when: "afterChildren",
							}}
						>
							{restaurantIllustrations.length > 0 && (
								<div
									className={`restaurantTopMarqueeIllustrationsContainer ${restaurant?.fields?.id}-illustrationsContainer`}
								>
									{restaurantIllustrations.map((image, i) => (
										<DiagonalReveal
											key={"restaurantCardIllustration" + i}
											className="restaurantIllustration"
											duration={1}
											delay={0.325}
											index={i}
										>
											<GatsbyImage
												image={getImage(image)}
												alt=""
												loading="eager"
											/>
										</DiagonalReveal>
									))}
								</div>
							)}
						</motion.div>
					</AnimatePresence>

					{children}
					<CookiesBanner />
				</main>

				<Footer />
			</div>
		</SmoothScroll>
	);
}
